/**
 * The price: Grow More first, then Free.
 *
 * Every price, date and phase is the server's (the landing read, GET
 * /billing/landing, from the owner's Grow settings and Stripe's prices by the
 * server's clock), so the heading and the Grow More card follow the
 * calendar: the launch prices before the new ones start ($39 a year, $7.99 a
 * month), the launch line from the day the owner allows it (the existing
 * server's finished launch sentence, facts.launchWords: the day and the launch price, never the price after it, L6).
 * Founding gardeners see their own terms and never the launch line, the
 * 14-day trial line or a trial button (they are free until the founding date
 * instead; the Grow More button says "Claim your founding place", the
 * existing founding words, B69; the server sends which: facts.startKey). Their first-year price is drawn as the
 * server says it, whole and in the page's language (facts.founding.priceWords, C101); the app builds none.
 *
 * What each card lists is the server's plan comparison (facts.rows, the rule
 * that enforces each row): the Grow More card names what Grow More adds (a
 * row whose Grow More cell differs from Free's), the Free card what Free has
 * (a row whose Free cell is not "no"). CARD_WORDS only says which of the
 * page's own words each row is drawn with, so a row the server takes away
 * from a plan leaves its card.
 *
 * The heading is the same sentence at every price, "$39 a year. Less than a
 * flat of tomato plants." (owner, 2026-10-08); only the number changes.
 *
 * The remodelled page (landing.V2) differs as the owner's list says: an
 * "Off-season price, until April 1" pill above the heading and the line "Join
 * before April 1 and lock in $39 for your first year, or $7.99 a month." under it (both left out for founding
 * visitors, and once the later prices are in force); the Grow More list of
 * five, its trial line and "Start your free trial"; the Free card renamed
 * "Free plan" with five lines and "Use the Free plan"; and "Cancel or pause
 * any time." below. The prices, the date and the days are the server's.
 *
 * Words (en/es): landing.price.*, landing.v2.price.*, and billing.plan.perYear
 * and .orMonthly, which the subscribe page uses too.
 *
 * @module Price
 */

import React from 'react';
import { Snowflake } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { PlanRow } from '@/shared/api/billing';
import { pauseDay } from '@/features/billing/lib/pauseDay';
import { foundingDay } from '@/features/billing/lib/foundingDay';
import { usd, usdOf } from '../lib/landingFacts';
import { LandingBlock } from '../components/LandingBlock';
import type { LandingSectionProps } from '../landingTypes';

/** The page's words for the rows of one card: a row's key to the lines it is drawn as (translation keys), in order. */
type CardWords = Readonly<Record<string, readonly string[]>>;

/**
 * The page's own words for each row of the server's plan comparison, on each
 * card of the current page and the remodelled one. A row with no words here
 * is not named on that card (the comparison on the subscribe page names
 * every row). Which rows a card shows is the server's.
 */
const CARD_WORDS: Readonly<Record<'more' | 'free' | 'moreV2' | 'freeV2', CardWords>> = {
  more: {
    plants: ['landing.price.more.plants'],
    warningEmails: ['landing.price.more.warnings'],
    ai: ['landing.price.more.ai'],
  },
  free: {
    plants: ['landing.price.free.plants'],
    today: ['landing.price.free.today'],
    sharing: ['landing.price.free.share'],
  },
  moreV2: {
    plants: ['landing.price.more.plants'],
    warningEmails: ['landing.v2.price.more.warnings'],
    ai: ['landing.v2.price.more.doctor', 'landing.v2.price.more.packets', 'landing.v2.price.more.photos'],
  },
  freeV2: {
    plants: ['landing.price.free.plants'],
    today: ['landing.price.free.today', 'landing.v2.price.free.photos'],
    sharing: ['landing.v2.price.free.share'],
    monday: ['landing.v2.price.free.monday'],
  },
};

/**
 * A card's lines: the words of each row it shows, in the server's order.
 *
 * @param rows - The server's plan comparison.
 * @param shows - Whether the card shows a row.
 * @param words - The page's words for the card's rows.
 * @returns The lines' translation keys.
 */
function cardLines(rows: readonly PlanRow[], shows: (row: PlanRow) => boolean, words: CardWords): string[] {
  return rows.filter(shows).flatMap((row) => words[row.key] ?? []);
}

/**
 * Whether the Grow More card names a row: what Grow More adds to Free.
 *
 * @param row - A row of the comparison.
 */
const growMoreAdds = (row: PlanRow): boolean => row.grow !== false && row.grow !== row.free;

/**
 * Whether the Free card names a row: what Free has.
 *
 * @param row - A row of the comparison.
 */
const freeHas = (row: PlanRow): boolean => row.free !== false;

/**
 * The price section.
 *
 * @param props.facts - The prices and dates in force.
 * @param props.onStart - Starts a garden.
 * @param props.v2 - Whether this is the remodelled page.
 */
export const Price: React.FC<LandingSectionProps> = ({ facts, onStart, v2 }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const founding = facts.founding;
  /** A YYYY-MM-DD date said the page's way ("June 30"). */
  const day = (iso: string): string => pauseDay(`${iso}T12:00:00`, language);
  // The founder price said whole by the server, in the page's language (C101).
  const foundingPrice = founding?.priceWords ?? (founding ? usdOf(founding.price) : null);
  const { year, month } = facts.prices;
  // The same comparison at every price; only the number follows the date.
  const heading = t('landing.price.headline', { price: usdOf(year) });
  // The remodelled page's off-season pill and line: for everyone but founding visitors, until the later prices start.
  const offSeason = v2 && !founding && facts.phase !== 'after';
  const moreLines = cardLines(facts.rows, growMoreAdds, v2 ? CARD_WORDS.moreV2 : CARD_WORDS.more);
  const freeLines = cardLines(facts.rows, freeHas, v2 ? CARD_WORDS.freeV2 : CARD_WORDS.free);
  return (
    <LandingBlock
      title={heading}
      single
      eyebrow={offSeason && (
        <p className="inline-flex items-center gap-1.5 rounded-full bg-[#A9532F] px-3 py-1 text-sm font-bold text-white">
          <Snowflake className="h-4 w-4" aria-hidden="true" />
          {t('landing.v2.price.pill', { date: day(facts.launchEnds) })}
        </p>
      )}
    >
      {founding && (
        <p className="max-w-prose">
          <b>{t('landing.price.foundingLead')}</b> {t('landing.price.foundingBody', { date: foundingDay(founding.freeUntil, language), price: foundingPrice ?? '' })}
          {founding.startsWords && <> {founding.startsWords}</>}
        </p>
      )}
      {/* The launch sentence is the server's, finished (L6): the day and the launch price, never the price after it. */}
      {facts.launchWords && (offSeason || (!v2 && facts.phase === 'launchLine' && !founding)) && (
        <p className="max-w-prose">{facts.launchWords}</p>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="space-y-2 rounded-2xl border-2 border-primary bg-card p-5 shadow-xl">
          <h3 className="font-grow text-xl font-bold">{t('landing.price.more.name')}</h3>
          <p>
            <span className="font-grow text-3xl font-bold tabular-nums">{usdOf(year)}</span>{' '}
            <span className="text-sm text-muted-foreground">{t('billing.plan.perYear')} &middot; {t('billing.plan.orMonthly', { price: usdOf(month) })}</span>
          </p>
          <ul className="list-disc space-y-1 pl-5">
            {moreLines.map((key) => <li key={key}>{t(key)}</li>)}
          </ul>
          {/* A founding visitor is free until the founding date, so the 14-day trial line would be a second free offer. */}
          {!founding && (
            <p className="text-sm text-muted-foreground">{t(v2 ? 'landing.v2.price.more.trial' : 'landing.price.more.trial', { days: facts.trialDays })}</p>
          )}
          <Button className="w-full" size="lg" onClick={onStart}>{t(facts.startKey ?? (v2 ? 'landing.v2.price.more.cta' : 'landing.price.more.cta'))}</Button>
        </div>
        <div className="space-y-2 rounded-2xl border bg-card p-5 shadow-sm">
          <h3 className="font-grow text-xl font-bold">{t(v2 ? 'landing.v2.price.free.name' : 'landing.price.free.name')}</h3>
          <p><span className="font-grow text-3xl font-bold tabular-nums">{usd(0)}</span></p>
          <ul className="list-disc space-y-1 pl-5">
            {freeLines.map((key) => <li key={key}>{t(key, { plants: facts.freePlants })}</li>)}
          </ul>
          <Button className="w-full" size="lg" variant="outline" onClick={onStart}>{t(v2 ? 'landing.v2.price.free.cta' : 'landing.price.free.cta')}</Button>
        </div>
      </div>
      {v2 && <p className="text-center text-muted-foreground">{t('landing.v2.price.cancel')}</p>}
    </LandingBlock>
  );
};
